import React from 'react';

/**
 * Universal button handler utilities
 * Handles special button behaviors like "Join Now" modal opening
 */

/**
 * Checks if a button should open the join modal based on its text
 * @param {string} buttonText - The text content of the button
 * @returns {boolean} - True if button should open join modal
 */
export const shouldOpenJoinModal = (buttonText) => {
	if (!buttonText) return false;
	return buttonText.toLowerCase().includes('join now');
};

/**
 * Opens the join modal by dispatching a custom event
 * @param {string} prefilledEmail - Optional email to prefill in the modal
 */
export const openJoinModal = (prefilledEmail = '') => {
	if (typeof window !== 'undefined') {
		const event = new CustomEvent('openLoginModal', {
			detail: {
				type: 'join',
				prefilledEmail,
			},
		});
		window.dispatchEvent(event);
	}
};

/**
 * Universal button click handler that checks for special button behaviors
 * @param {Event} e - The click event
 * @param {string} buttonText - The text content of the button
 * @param {Function} customHandler - Optional custom click handler
 * @param {string} prefilledEmail - Optional email to prefill in join modal
 */
export const handleButtonClick = (
	e,
	buttonText,
	customHandler = null,
	prefilledEmail = ''
) => {
	// Check if this is a "Join Now" button
	if (shouldOpenJoinModal(buttonText)) {
		e.preventDefault();
		openJoinModal(prefilledEmail);
		return;
	}

	// If custom handler is provided, use it
	if (customHandler) {
		customHandler(e);
		return;
	}

	// Default behavior - let the event bubble up
};

/**
 * Higher-order function that wraps any button component with universal handling
 * @param {React.Component} ButtonComponent - The button component to wrap
 * @returns {React.Component} - Wrapped component with universal handling
 */
export const withUniversalButtonHandling = (ButtonComponent) => {
	return (props) => {
		const { buttonName, buttonText, onClick, ...otherProps } = props;

		const handleClick = (e) => {
			const text = buttonName || buttonText || '';
			handleButtonClick(e, text, onClick);
		};

		return <ButtonComponent {...otherProps} onClick={handleClick} />;
	};
};

/**
 * Determines if a button should be rendered without a link (for Join Now buttons)
 * @param {string} buttonName - The button text
 * @param {string} buttonLink - The button link
 * @returns {boolean} - True if button should render without link
 */
export const shouldRenderWithoutLink = (buttonName, buttonLink) => {
	return (
		shouldOpenJoinModal(buttonName) || !buttonLink || buttonLink.trim() === ''
	);
};

/**
 * Gets the appropriate link for a button (empty for Join Now buttons)
 * @param {string} buttonName - The button text
 * @param {string} buttonLink - The button link
 * @returns {string} - The link to use (empty for Join Now buttons)
 */
export const getButtonLink = (buttonName, buttonLink) => {
	return shouldOpenJoinModal(buttonName) ? '' : buttonLink || '';
};

/**
 * Checks if a button should prevent default link behavior (for Join Now buttons)
 * @param {string} buttonName - The button text
 * @param {string} buttonLink - The button link
 * @returns {boolean} - True if button should prevent default link behavior
 */
export const shouldPreventDefaultLink = (buttonName, buttonLink) => {
	return shouldOpenJoinModal(buttonName);
};
